import { useSelector } from 'react-redux';
import { salesPageSelectors } from '@/Pages/Sales/services';
import { documentPrintModalSelectors } from '@/ModalWindows/DocumentPrintModal/services/selectors';
import { useMemo } from 'react';
import { useDocumentPrintOrderTypeOptions } from '@/Enums/DocumentPrintOrderTypeHooks';
import { NumberGenerationType } from '@/Enums';
import { orderUtils } from '@/Utils/models/Order';
import { useLocalization } from '@/Hooks';

export function useSelectTemplateOptions() {
  const {
    modalWindows: {
      documentPrintModal: { SelectTemplateModal },
    },
    models: {
      OrderTableRow: { CancelInvoiceNumber },
    },
  } = useLocalization();

  const selectedSale = useSelector(salesPageSelectors.selectedSale);
  const documentType = useSelector(documentPrintModalSelectors.documentType);
  const documentNumbers = useSelector(documentPrintModalSelectors.selectedTypeNumbers);
  const selectedTemplate = useSelector(documentPrintModalSelectors.selectedDocumentTemplate);

  const orderTypeOptions = useDocumentPrintOrderTypeOptions();

  const documentCreateState = useMemo(() => {
    switch (documentType) {
      case NumberGenerationType.CancelInvoice: {
        const noCancelInvoices = orderUtils.getCancelInvoiceNumbers(selectedSale).length == 0;
        const cancelInvoiceDisabled = orderUtils.isCreatingCancelInvoiceDisabled(selectedSale);
        const isDisabled = noCancelInvoices && cancelInvoiceDisabled;

        return {
          disabled: isDisabled,
          message: isDisabled ? CancelInvoiceNumber.NoInvoicesCreatedYetMessage : '',
        };
      }

      default: {
        return {
          disabled: false,
          message: '',
        };
      }
    }
  }, [selectedSale, documentType]);

  const { isSortActive, isPrintDisabled } = useMemo(() => {
    const isMultiDocumentPrint = documentNumbers.length > 1 && Boolean(documentType);

    if (!isMultiDocumentPrint) {
      return { isSortActive: false, isPrintDisabled: false };
    }

    const hasMultipleNumbers = documentNumbers.some(({ numbers }) => numbers.length > 1);
    const templateSortActive = Boolean(selectedTemplate?.isSortWhenPrinting && selectedTemplate?.countCopy != 1);

    const isSortActive = hasMultipleNumbers && templateSortActive;
    const isPrintDisabled = documentNumbers.some(({ numbers }) => numbers.length == 0);

    return {
      isSortActive,
      isPrintDisabled,
    };
  }, [selectedTemplate]);

  const documentPrintState = useMemo(() => {
    if (isPrintDisabled) {
      return {
        disabled: true,
        message: SelectTemplateModal.PrintDisabled,
      };
    }

    return documentCreateState;
  }, [documentCreateState, isPrintDisabled]);

  return {
    orderTypeOptions,
    isSortActive,
    documentPrintState,
  };
}
